import * as React from "react"
import { cn } from "../../lib/utils"
import { Spinner } from "./spinner"

interface ListProps<T> extends Omit<React.HTMLAttributes<HTMLUListElement>, 'children'> {
  dataSource?: T[]
  renderItem?: (item: T, index: number) => React.ReactNode
  loading?: boolean
  header?: React.ReactNode
  footer?: React.ReactNode
  rowKey?: string | ((item: T) => string)
  emptyText?: React.ReactNode
}

function List<T>({
  className,
  dataSource = [],
  renderItem,
  loading = false,
  header,
  footer,
  rowKey,
  emptyText = "No data",
  ...props
}: ListProps<T>) {
  const getKey = (item: T, index: number): string => {
    if (typeof rowKey === 'function') {
      return rowKey(item)
    }
    if (typeof rowKey === 'string' && item && typeof item === 'object') {
      return (item as Record<string, unknown>)[rowKey] as string
    }
    return String(index)
  }

  return (
    <div className="w-full">
      {header && (
        <div className="px-4 py-3 font-medium border-b">{header}</div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : dataSource.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          {emptyText}
        </div>
      ) : (
        <ul className={cn("divide-y", className)} {...props}>
          {dataSource.map((item, index) => (
            <React.Fragment key={getKey(item, index)}>
              {renderItem?.(item, index)}
            </React.Fragment>
          ))}
        </ul>
      )}
      {footer && (
        <div className="px-4 py-3 border-t">{footer}</div>
      )}
    </div>
  )
}

interface ListItemProps extends React.LiHTMLAttributes<HTMLLIElement> {}

const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <li
        ref={ref}
        className={cn("px-4 py-3", className)}
        {...props}
      />
    )
  }
)

ListItem.displayName = "ListItem"

// Attach ListItem as a static property
const ListWithItem = List as typeof List & { Item: typeof ListItem }
ListWithItem.Item = ListItem

export { ListWithItem as List, ListItem }
